import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { HeadingNode, QuoteNode, $createHeadingNode } from '@lexical/rich-text';
import { ListItemNode, ListNode, INSERT_UNORDERED_LIST_COMMAND } from '@lexical/list';
import {
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $getSelection,
  $isRangeSelection,
  EditorState,
  FORMAT_TEXT_COMMAND,
  LexicalEditor as LexicalEditorType
} from 'lexical';
import { $setBlocksType } from '@lexical/selection';
import { Button, ButtonGroup } from 'react-bootstrap';

const EMPTY_EDITOR_STATE =
  '{"root":{"children":[{"children":[],"direction":null,"format":"","indent":0,"type":"paragraph","version":1}],"direction":null,"format":"","indent":0,"type":"root","version":1}}';

function isLexicalJson(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed.startsWith('{')) return false;
  try {
    const parsed = JSON.parse(trimmed) as { root?: unknown };
    return !!parsed?.root;
  } catch {
    return false;
  }
}

/** Plain text from Lexical JSON, TinyMCE/HTML, or plain text. */
export function extractComplaintPlainText(content: string | null | undefined): string {
  if (!content?.trim()) return '';
  const trimmed = content.trim();

  if (trimmed.startsWith('{')) {
    try {
      const parsed = JSON.parse(trimmed) as {
        root?: { children?: Array<{ text?: string; children?: unknown[] }> };
      };
      const walk = (node: unknown): string => {
        if (!node || typeof node !== 'object') return '';
        const n = node as { text?: string; children?: unknown[] };
        let text = typeof n.text === 'string' ? n.text : '';
        if (Array.isArray(n.children)) {
          text += n.children.map(walk).join('');
        }
        return text;
      };
      return walk(parsed.root).trim();
    } catch {
      // fall through
    }
  }

  const withBreaks = trimmed
    .replace(/<\s*br\s*\/?>/gi, '\n')
    .replace(/<\/\s*p\s*>/gi, '\n')
    .replace(/<\/\s*div\s*>/gi, '\n')
    .replace(/<\/\s*li\s*>/gi, '\n');
  const withoutTags = withBreaks.replace(/<[^>]+>/g, ' ');
  const decoded = withoutTags
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'");
  return decoded.replace(/\s+/g, ' ').trim();
}

/** @deprecated Use extractComplaintPlainText */
export const extractLexicalPlainText = extractComplaintPlainText;

/** Render Lexical/HTML/plain content as readable HTML (staff-authored). */
export function RichHtmlContent({
  html,
  empty = '—'
}: {
  html?: string | null;
  empty?: string;
}) {
  const raw = html?.trim() || '';
  const plain = extractComplaintPlainText(raw);
  if (!plain) {
    return <span className="text-muted">{empty}</span>;
  }

  if (raw.startsWith('{') || !raw.includes('<')) {
    return <span style={{ whiteSpace: 'pre-wrap' }}>{plain}</span>;
  }

  return (
    <div
      className="rich-html-content"
      style={{ lineHeight: 1.45 }}
      dangerouslySetInnerHTML={{ __html: raw }}
    />
  );
}

type ComplaintEditorProps = {
  value: string;
  onChange: (serialized: string, plainText: string) => void;
  /** When set, shows a character counter and callers can enforce the limit. */
  maxPlainTextLength?: number;
  disabled?: boolean;
  placeholder?: string;
  /** Kept for call-site compatibility; Lexical uses minHeight instead. */
  height?: number;
};

function EditorErrorBoundary({ children }: { children: React.JSX.Element }) {
  return (
    <LexicalErrorBoundary onError={(error) => console.error(error)}>
      {children}
    </LexicalErrorBoundary>
  );
}

function toEditorStateJson(value: string): string {
  if (!value?.trim()) return EMPTY_EDITOR_STATE;
  if (isLexicalJson(value)) return value.trim();

  const plain = extractComplaintPlainText(value);
  if (!plain) return EMPTY_EDITOR_STATE;

  return JSON.stringify({
    root: {
      children: [
        {
          children: [
            {
              detail: 0,
              format: 0,
              mode: 'normal',
              style: '',
              text: plain,
              type: 'text',
              version: 1
            }
          ],
          direction: null,
          format: '',
          indent: 0,
          type: 'paragraph',
          version: 1
        }
      ],
      direction: null,
      format: '',
      indent: 0,
      type: 'root',
      version: 1
    }
  });
}

function InitialStatePlugin({ value }: { value: string }) {
  const [editor] = useLexicalComposerContext();
  const lastApplied = useRef<string | null>(null);

  useEffect(() => {
    const next = toEditorStateJson(value);
    if (lastApplied.current === next) return;

    try {
      const current = JSON.stringify(editor.getEditorState().toJSON());
      if (current === next) {
        lastApplied.current = next;
        return;
      }
      const state = editor.parseEditorState(next);
      editor.setEditorState(state);
      lastApplied.current = next;
    } catch {
      editor.update(() => {
        const root = $getRoot();
        root.clear();
        const paragraph = $createParagraphNode();
        const plain = extractComplaintPlainText(value);
        if (plain) paragraph.append($createTextNode(plain));
        root.append(paragraph);
      });
      lastApplied.current = next;
    }
  }, [editor, value]);

  return null;
}

function EditablePlugin({ disabled }: { disabled?: boolean }) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    editor.setEditable(!disabled);
  }, [disabled, editor]);

  return null;
}

function ToolbarPlugin({ disabled }: { disabled?: boolean }) {
  const [editor] = useLexicalComposerContext();

  const formatBold = () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
  const formatItalic = () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
  const formatBullet = () =>
    editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
  const formatHeading = () => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $setBlocksType(selection, () => $createHeadingNode('h4'));
      }
    });
  };

  return (
    <ButtonGroup size="sm" className="mb-2">
      <Button variant="outline-secondary" disabled={disabled} onClick={formatBold} type="button">
        Bold
      </Button>
      <Button variant="outline-secondary" disabled={disabled} onClick={formatItalic} type="button">
        Italic
      </Button>
      <Button variant="outline-secondary" disabled={disabled} onClick={formatBullet} type="button">
        List
      </Button>
      <Button variant="outline-secondary" disabled={disabled} onClick={formatHeading} type="button">
        Heading
      </Button>
    </ButtonGroup>
  );
}

const ComplaintEditor: React.FC<ComplaintEditorProps> = ({
  value,
  onChange,
  maxPlainTextLength,
  disabled = false,
  placeholder = 'Describe the customer concern...',
  height = 220
}) => {
  const [plainLength, setPlainLength] = useState(
    () => extractComplaintPlainText(value).length
  );

  const initialConfig = useMemo(
    () => ({
      namespace: 'VehicleServiceComplaint',
      editable: !disabled,
      theme: {
        paragraph: 'mb-1',
        text: {
          bold: 'fw-bold',
          italic: 'fst-italic'
        },
        list: {
          ul: 'mb-1',
          listitem: 'ms-3'
        },
        heading: {
          h4: 'h5 mb-1'
        }
      },
      onError: (error: Error) => {
        console.error(error);
      },
      nodes: [HeadingNode, QuoteNode, ListNode, ListItemNode],
      editorState: toEditorStateJson(value)
    }),
    // Mount-only: later value updates are applied by InitialStatePlugin.
    []
  );

  const handleChange = useCallback(
    (editorState: EditorState, editor: LexicalEditorType) => {
      editorState.read(() => {
        const plain = $getRoot().getTextContent().trim();
        const serialized = JSON.stringify(editor.getEditorState().toJSON());
        setPlainLength(plain.length);
        onChange(serialized, plain);
      });
    },
    [onChange]
  );

  useEffect(() => {
    setPlainLength(extractComplaintPlainText(value).length);
  }, [value]);

  return (
    <div className={`border rounded p-2 bg-white ${disabled ? 'opacity-75' : ''}`}>
      <LexicalComposer initialConfig={initialConfig}>
        <ToolbarPlugin disabled={disabled} />
        <div className="position-relative">
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                className="form-control border-0 shadow-none"
                style={{ minHeight: height, outline: 'none' }}
              />
            }
            placeholder={
              <div
                className="position-absolute text-muted"
                style={{ top: 8, left: 12, pointerEvents: 'none', fontSize: 12.8 }}
              >
                {placeholder}
              </div>
            }
            ErrorBoundary={EditorErrorBoundary}
          />
        </div>
        <HistoryPlugin />
        <ListPlugin />
        <OnChangePlugin onChange={handleChange} />
        <InitialStatePlugin value={value} />
        <EditablePlugin disabled={disabled} />
      </LexicalComposer>
      {typeof maxPlainTextLength === 'number' && (
        <div
          className={`small text-end mt-1 ${
            plainLength > maxPlainTextLength ? 'text-danger' : 'text-muted'
          }`}
        >
          {plainLength}/{maxPlainTextLength} characters
        </div>
      )}
    </div>
  );
};

export default ComplaintEditor;
